import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Header } from '@amzn/awsui-components-react-v3';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
    selectIsLoading,
    updateSelectedActivity,
} from '../../../store/slices/selectedActivitySlice';
import { useNotifications } from '../../../../common/context/grimsbyNotifications';
import useFormValidation, {
    ValidationType,
} from '../../../../common/utils/formValidation';
import { ActivityData } from '../../../interfaces/activity';
import { handleScrollToError } from '../../../../common/utils/scrollToError';
import {
    ActivityLMSType,
    ActivityModality,
    ActivityProgram,
    ActivitySessionControlArrayFormValues,
    ActivityStatus,
    AudienceType,
    BasicActivityFormSectionProps,
    isAmsRmsAutoCountLmsType,
    validateClassSizeConsistency,
    validateMyClassCustomerAccountFields,
} from '../Common/Common';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import EditBaseDetailsFormSection, {
    EditBaseDetailsFormSectionProps,
} from '../FormSections/EditBaseDetailsFormSection/EditBaseDetailsFormSection';
import EditBaseDetailsCancellationModal from './EditBaseDetailsModals/EditBaseDetailsCancellationModal';
import CancelModal, {
    CancelModalProps,
} from '../../../../common/components/CancelModal/CancelModal';
import useFormDirtyState from '../../../../common/hooks/useFormDirtyState';
import { ACTIVITY_VALIDATION_FIELDS } from '../Common/Validations';
import { validateClassroomClassSizeCap } from '../Common/classroomEligibilityRules';
import {
    UpdateActivityErrorMessage,
    UpdateActivityOrderServiceRejectedMessage,
    UpdateActivityVersionConflictMessage,
} from '../../Common/constants/flashContent';
import { isOrderServiceBillingRejection } from '../../../utils/orderServiceErrors';
import { ensureViltTypeForSubmission } from '../../../utils/viltTypeUtils';

const MAX_ACTIVITY_NAME_LENGTH = 150;
const ACTIVITY_NAME_LENGTH_ERROR = `Activity name cannot be more than ${MAX_ACTIVITY_NAME_LENGTH} characters.`;
const MYCLASS_CLASS_SIZE_REQUIRED_ERROR =
    'Class size is required and must be greater than 0 to set a myClass activity to Active.';

const editBaseDetailsValidationConfig: {
    [key in ValidationType]?: Array<keyof ActivityData>;
} = {
    required: [
        'activity_status',
        'activity_audience',
        'activity_modality',
        'activity_name',
    ],
    positiveInteger: ['class_size'],
};

export const defaultShowAdditionalFields = {
    showDeliveryAddress: false,
    showNumberOfStudentsAttended: false,
    showPartnerInitiative: false,
    showLMSType: false,
    showClassSize: false,
    showVILTType: false,
    showInstructorWarning: false,
};

/**
 * Computes which additional fields should be visible based on form values
 * and initial form state. Used by both the parent (on mount) and child
 * (on formValues change) to avoid duplicated visibility logic.
 */
export const computeAdditionalFieldsVisibility = (
    formValues: Pick<
        ActivityData,
        | 'activity_status'
        | 'activity_modality'
        | 'activity_audience'
        | 'program'
    > &
        Partial<Pick<ActivityData, 'lms_type'>>,
    initialFormState: Pick<
        ActivityData,
        'delivery_address_1' | 'delivery_postal_code' | 'attended'
    >,
    isManualMarkAttendanceNeeded: boolean,
) => {
    const { activity_status, activity_modality, activity_audience, program } =
        formValues;
    const isPartnerProgram = program === ActivityProgram.Partner;
    const isCommercialProgram = program === ActivityProgram.Commercial;
    const isCompleted = activity_status === ActivityStatus.Completed;
    const isILT = activity_modality === ActivityModality.ILT;
    const isvILT = activity_modality === ActivityModality.vILT;
    const isHybrid = activity_modality === ActivityModality.Hybrid;
    const isActive = activity_status === ActivityStatus.Active;
    const isPartnerPrivate = activity_audience === AudienceType.PartnerPrivate;
    const isCommercialPrivate =
        activity_audience === AudienceType.CommercialPrivate;
    const isCommercialPublic =
        isCommercialProgram && activity_audience === AudienceType.Public;

    // Resolve the status / modality / program / audience matrix. Every branch
    // returns, so this is wrapped to give the lms_type rule below a single
    // place to apply rather than repeating it across each branch.
    const fields = (() => {
        // non-auto-count Activity is set to `Completed` with no attended value
        if (
            isManualMarkAttendanceNeeded &&
            isCompleted &&
            !initialFormState.attended
        ) {
            return {
                ...defaultShowAdditionalFields,
                showNumberOfStudentsAttended: true,
            };
        }

        if (isActive) {
            if (isILT) {
                const showDeliveryAddress =
                    !initialFormState.delivery_address_1 ||
                    !initialFormState.delivery_postal_code;

                if (isCommercialProgram) {
                    return {
                        ...defaultShowAdditionalFields,
                        showDeliveryAddress,
                        showClassSize: true,
                        showLMSType: true,
                    };
                }

                if (isPartnerProgram) {
                    return {
                        ...defaultShowAdditionalFields,
                        showDeliveryAddress,
                        showClassSize: isPartnerPrivate,
                        showLMSType: true,
                        showPartnerInitiative: true,
                    };
                }

                return {
                    ...defaultShowAdditionalFields,
                    showDeliveryAddress,
                    showLMSType: true,
                };
            }

            if (isvILT) {
                if (isCommercialProgram) {
                    return {
                        ...defaultShowAdditionalFields,
                        showLMSType: isCommercialPrivate,
                        showVILTType: isCommercialPrivate,
                        showClassSize: isCommercialPrivate,
                    };
                }

                if (isPartnerProgram) {
                    return {
                        ...defaultShowAdditionalFields,
                        showLMSType: isPartnerPrivate,
                        showVILTType: isPartnerPrivate,
                        showPartnerInitiative: true,
                    };
                }

                return {
                    ...defaultShowAdditionalFields,
                };
            }

            if (isHybrid) {
                const showDeliveryAddress =
                    !initialFormState.delivery_address_1 ||
                    !initialFormState.delivery_postal_code;

                if (isCommercialProgram) {
                    return {
                        ...defaultShowAdditionalFields,
                        showDeliveryAddress,
                        showClassSize:
                            isCommercialPrivate || isCommercialPublic,
                    };
                }

                if (isPartnerProgram) {
                    return {
                        ...defaultShowAdditionalFields,
                        showDeliveryAddress,
                        showPartnerInitiative: true,
                    };
                }

                return {
                    ...defaultShowAdditionalFields,
                    showDeliveryAddress,
                };
            }
        }

        // Non-active, non-completed — show partner initiative if applicable
        if (isPartnerProgram) {
            return {
                ...defaultShowAdditionalFields,
                showPartnerInitiative: true,
            };
        }

        return defaultShowAdditionalFields;
    })();

    // The schedule service requires class_size when a myClass activity is set
    // to Active. The matrix above only models the Commercial and Partner
    // programs, so programs such as Dedicated Cloud never surfaced the input
    // and the save failed server-side with no way to fix it in the form.
    // Scoped to Active because LMS type is only editable while Active, so that
    // is the only point at which an activity can be switched to myClass.
    if (isActive && formValues.lms_type === ActivityLMSType.MyClass) {
        return {
            ...fields,
            showClassSize: true,
        };
    }

    return fields;
};

const EditBaseDetailsForm = ({
    initialFormState,
}: {
    initialFormState: ActivityData;
}) => {
    const match = useRouteMatch<{ id: string }>();
    const isLoading = useSelector(selectIsLoading);
    const history = useHistory();
    const dispatch = useDispatch();
    const { addNotification, removeAllNotifications } = useNotifications();

    const { isInvalid, errors, validateForm } = useFormValidation<
        ActivityData,
        ActivitySessionControlArrayFormValues
    >();

    const [activityNameValidationError, setActivityNameValidationError] =
        useState<string>('');
    const [classSizeValidationError, setClassSizeValidationError] =
        useState<string>('');
    const [activityStatusValidationError, setActivityStatusValidationError] =
        useState<string>('');
    const [formValues, setFormValues] =
        useState<ActivityData>(initialFormState);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const isDirty = useFormDirtyState(initialFormState, formValues);
    const [cancellationReason, setCancellationReason] =
        useState<OptionDefinition | null>(null);
    const [showCancelationReason, setShowCancelationReason] = useState(false);
    const [showAdditionalFields, setShowAdditionalFields] = useState(
        defaultShowAdditionalFields,
    );
    const [dontRemoveData, setDontRemoveData] = useState(false);
    const [cancelFeeType, setCancelFeeType] = useState<string | null>(null);
    const isManualMarkAttendanceNeeded = !isAmsRmsAutoCountLmsType(
        initialFormState.lms_type,
    );
    const showRemoveDataCheckbox =
        initialFormState.billing_invoices?.length > 0 ||
        initialFormState.customers?.length > 0;

    const navigateToDetailPage = () => {
        history.push({
            pathname: `/activities/${match.params.id}`,
        });
    };

    const getSuccessfulUpdateText = () => {
        let updateText = 'The activity has been updated.';

        if (formValues.activity_status === ActivityStatus.Canceled) {
            if (!dontRemoveData) {
                updateText =
                    'Activity has been canceled and assigned instructors have been released, and customer and invoice data has been removed.';
            } else {
                updateText =
                    'Activity has been canceled and assigned instructors have been released.';
            }
        }

        return updateText;
    };

    useEffect(() => {
        removeAllNotifications();
    }, [removeAllNotifications]);

    useEffect(() => {
        setShowAdditionalFields(
            computeAdditionalFieldsVisibility(
                initialFormState,
                initialFormState,
                isManualMarkAttendanceNeeded,
            ),
        );
    }, [
        initialFormState.activity_status,
        initialFormState.activity_modality,
        initialFormState.attended,
        initialFormState.delivery_address_1,
        initialFormState.partner_initiative,
        initialFormState.activity_audience,
        initialFormState.program,
        initialFormState.lms_type,
    ]);

    const handleFieldEvent = useCallback((changes: Partial<ActivityData>) => {
        // Validate the activity name length as the user types, surfacing/clearing
        // the inline error immediately rather than waiting for submit.
        if ('activity_name' in changes) {
            setActivityNameValidationError(
                (changes.activity_name?.length ?? 0) > MAX_ACTIVITY_NAME_LENGTH
                    ? ACTIVITY_NAME_LENGTH_ERROR
                    : '',
            );
        }
        // Clear class size validation error when user modifies class_size, or
        // when they change either input the myClass class size rule depends on
        // (re-validated on submit)
        if (
            'class_size' in changes ||
            'lms_type' in changes ||
            'activity_status' in changes
        ) {
            setClassSizeValidationError('');
        }
        // Clear the myClass account-fields error when the user changes status (re-validated on submit)
        if ('activity_status' in changes) {
            setActivityStatusValidationError('');
        }
        setFormValues((values) => ({
            ...values,
            ...changes,
        }));
    }, []);

    // https://quip-amazon.com/fLHPAp8B8bUU/Activity-Status-Validations
    const getValidationConfig = useCallback(
        (
            formValues,
        ): {
            [key in ValidationType]?: Array<keyof ActivityData>;
        } => {
            const {
                program,
                activity_audience,
                activity_status,
                activity_modality,
            } = formValues;
            const isCompleted = activity_status === ActivityStatus.Completed;
            const isCommercialProgram = program === ActivityProgram.Commercial;
            const isPartnerProgram = program === ActivityProgram.Partner;
            const isActive = activity_status === ActivityStatus.Active;
            const isILT = activity_modality === ActivityModality.ILT;
            const isVILT = activity_modality === ActivityModality.vILT;
            const isHybrid = activity_modality === ActivityModality.Hybrid;
            const isPrivate =
                activity_audience === AudienceType.CommercialPrivate ||
                activity_audience === AudienceType.PartnerPrivate;
            const isCommercialPrivate =
                activity_audience === AudienceType.CommercialPrivate;
            const isCommercialPublic =
                isCommercialProgram &&
                activity_audience === AudienceType.Public;
            if (isCompleted) {
                return {
                    // attended
                    required:
                        ACTIVITY_VALIDATION_FIELDS.ACTIVITY_COMPLETED_DETAILS,
                };
            }

            if (isActive) {
                if (isILT) {
                    if (isCommercialProgram) {
                        return {
                            // delivery_address_1 delivery_postal_code class_size
                            required:
                                ACTIVITY_VALIDATION_FIELDS.ACTIVE_COMMERCIAL_ILT,
                            positiveInteger: ['class_size'],
                            maxValue: ['class_size'],
                        };
                    }

                    if (isPartnerProgram) {
                        if (!isPrivate) {
                            return {
                                // delivery_address_1 delivery_postal_code
                                required:
                                    ACTIVITY_VALIDATION_FIELDS.DELIVERY_DETAILS_REQUIRED,
                            };
                        }

                        if (isPrivate) {
                            return {
                                // delivery_address_1 delivery_postal_code class_size
                                required:
                                    ACTIVITY_VALIDATION_FIELDS.ACTIVE_PARTNER_PRIVATE_ILT,
                                positiveInteger: ['class_size'],
                                maxValue: ['class_size'],
                            };
                        }
                    }
                    return {
                        // delivery_address_1 delivery_postal_code
                        required:
                            ACTIVITY_VALIDATION_FIELDS.DELIVERY_DETAILS_REQUIRED,
                    };
                }

                if (isVILT) {
                    if (isCommercialProgram) {
                        if (isPrivate) {
                            return {
                                // class_size lms_type v_ilt_type
                                required:
                                    ACTIVITY_VALIDATION_FIELDS.ACTIVE_COMMERCIAL_PRIVATE_VILT,
                                positiveInteger: ['class_size'],
                                maxValue: ['class_size'],
                            };
                        }

                        return {
                            required:
                                ACTIVITY_VALIDATION_FIELDS.NO_VALIDATIONS_REQUIRED,
                        };
                    }

                    if (isPartnerProgram) {
                        if (isPrivate) {
                            return {
                                // lms_type v_ilt_type
                                required:
                                    ACTIVITY_VALIDATION_FIELDS.ACTIVE_PARTNER_PRIVATE_VILT,
                            };
                        }

                        return {
                            required:
                                ACTIVITY_VALIDATION_FIELDS.NO_VALIDATIONS_REQUIRED,
                        };
                    }

                    return {
                        required:
                            ACTIVITY_VALIDATION_FIELDS.NO_VALIDATIONS_REQUIRED,
                    };
                }

                if (isHybrid) {
                    if (isCommercialProgram) {
                        if (isCommercialPrivate || isCommercialPublic) {
                            return {
                                // delivery_address_1 delivery_postal_code class_size
                                required:
                                    ACTIVITY_VALIDATION_FIELDS.ACTIVE_COMMERCIAL_HYBRID_PUBLIC_PRIVATE,
                                positiveInteger: ['class_size'],
                                maxValue: ['class_size'],
                            };
                        }

                        return {
                            // delivery_address_1 delivery_postal_code
                            required:
                                ACTIVITY_VALIDATION_FIELDS.DELIVERY_DETAILS_REQUIRED,
                        };
                    }

                    if (isPartnerProgram) {
                        return {
                            // delivery_address_1 delivery_postal_code
                            required:
                                ACTIVITY_VALIDATION_FIELDS.DELIVERY_DETAILS_REQUIRED,
                        };
                    }
                    return {
                        // delivery_address_1 delivery_postal_code
                        required:
                            ACTIVITY_VALIDATION_FIELDS.DELIVERY_DETAILS_REQUIRED,
                    };
                }
            }

            return {
                required: ACTIVITY_VALIDATION_FIELDS.NO_VALIDATIONS_REQUIRED,
            };
        },
        [],
    );

    const handleAddCancellationReason = (e: any) => {
        handleFieldEvent({
            cancellation_reason: e.label,
        });
        setCancellationReason(e);
    };

    const handleSubmitEvent = async () => {
        setSubmitting(true);

        const invalid = validateForm(
            formValues,
            getValidationConfig(formValues),
        );

        if (invalid) {
            handleScrollToError();
            setSubmitting(false);
            return;
        }

        if (
            (formValues.activity_name?.length ?? 0) > MAX_ACTIVITY_NAME_LENGTH
        ) {
            setActivityNameValidationError(ACTIVITY_NAME_LENGTH_ERROR);
            setTimeout(() => handleScrollToError(), 0);
            setSubmitting(false);
            return;
        }

        // The schedule service rejects a myClass activity going Active without a
        // class size, so block it here with a field-level message. Scoped to
        // Active because that is the only status where the input is rendered.
        if (
            formValues.activity_status === ActivityStatus.Active &&
            formValues.lms_type === ActivityLMSType.MyClass &&
            !formValues.class_size
        ) {
            setClassSizeValidationError(MYCLASS_CLASS_SIZE_REQUIRED_ERROR);
            setTimeout(() => handleScrollToError(), 0);
            setSubmitting(false);
            return;
        }

        const classSizeCapError = validateClassroomClassSizeCap(formValues);
        if (classSizeCapError) {
            setClassSizeValidationError(classSizeCapError);
            setTimeout(() => handleScrollToError(), 0);
            setSubmitting(false);
            return;
        }

        // Validate class size consistency for semi-private activities
        const validation = validateClassSizeConsistency(formValues);
        if (!validation.isValid) {
            // Show error on the specific field (Cloudscape field-level validation)
            setClassSizeValidationError(
                `Class size mismatch: The activity class size (${validation.classSize ?? 'not set'}) does not match the sum of customer commitments (${validation.sum}). Please update either the class size or customer commitments.`,
            );
            // Scroll to error after DOM updates
            setTimeout(() => handleScrollToError(), 0);
            setSubmitting(false);
            return;
        }

        // A myClass activity cannot go Active until every customer has an SFDC account ID,
        // otherwise learners can see the class but cannot enroll. Block client-side with an
        // actionable, field-level message (the schedule service also enforces this server-side).
        if (!validateMyClassCustomerAccountFields(formValues)) {
            setActivityStatusValidationError(
                'This myClass activity cannot be set to Active until every customer has an ' +
                    'SFDC account ID. Without it, learners can see the class but cannot enroll. ' +
                    'Add the SFDC account for each customer on the Customers tab, then try again.',
            );
            setTimeout(() => handleScrollToError(), 0);
            setSubmitting(false);
            return;
        }

        const shouldRemoveRevenues =
            !dontRemoveData &&
            formValues.activity_status === ActivityStatus.Canceled;

        if (cancelFeeType !== null) {
            formValues.is_late_cancellation_fee =
                cancelFeeType === 'late_cancellation_fee' ? true : false;
            formValues.is_late_reschedule_fee =
                cancelFeeType === 'late_reschedule_fee' ? true : false;
        }

        if (shouldRemoveRevenues) {
            formValues.customers = formValues.customers
                ? formValues.customers.map((customer) => {
                      return {
                          ...customer,
                          revenues: [],
                      };
                  })
                : [];
            formValues.billing_invoices = [];
        }

        // Apply v_ilt_type defaulting for vILT/Hybrid activities before submission
        // This ensures v_ilt_type is properly set when lms_type is set but v_ilt_type wasn't explicitly selected
        const activityDataWithViltType =
            ensureViltTypeForSubmission(formValues);
        const activityData = { ...activityDataWithViltType };

        // For WebEx meeting fields (v_ilt_id, v_ilt_meeting_id), don't send them - managed by VCMS Lambda
        // However, v_ilt_type MUST be sent when it's being set for the first time (to trigger WebEx creation)
        // Only exclude v_ilt_type if it's unchanged from the initial state (already managed by backend)
        if (
            initialFormState.v_ilt_type &&
            activityData.v_ilt_type === initialFormState.v_ilt_type
        ) {
            delete activityData.v_ilt_type;
        }
        delete activityData.v_ilt_id;
        delete activityData.v_ilt_meeting_id;

        const result = await dispatch<any>(
            updateSelectedActivity(match.params.id, activityData),
        );

        const updateText = getSuccessfulUpdateText();

        addNotification({
            id: `update-activity`,
            ...(result.isSuccessful
                ? {
                      type: 'success',
                      content: updateText,
                  }
                : {
                      type: 'error',
                      content:
                          result.statusCode === 409 ? (
                              <UpdateActivityVersionConflictMessage />
                          ) : isOrderServiceBillingRejection(result) ? (
                              <UpdateActivityOrderServiceRejectedMessage
                                  detail={result.errorMessage}
                              />
                          ) : (
                              <UpdateActivityErrorMessage />
                          ),
                  }),
        });

        if (result.isSuccessful) {
            navigateToDetailPage();
        }
    };

    const getCancellationModal = async () => {
        if (
            formValues.activity_status === ActivityStatus.Canceled &&
            initialFormState.activity_status !== ActivityStatus.Canceled
        ) {
            setShowCancelationReason(true);
        } else {
            handleSubmitEvent();
        }
    };

    const editBaseDetailsFormSectionProps: EditBaseDetailsFormSectionProps = {
        initialFormState,
        formValues,
        errors: {
            ...errors,
            activity_name: activityNameValidationError || errors?.activity_name,
            class_size: classSizeValidationError || errors?.class_size,
            activity_status:
                activityStatusValidationError || errors?.activity_status,
        },
        handleFieldEvent,
        setShowAdditionalFields,
        showAdditionalFields,
        isManualMarkAttendanceNeeded,
        handleSubmitEvent,
    };

    const cancelModalProps: CancelModalProps = {
        isCancelModalVisible,
        setIsCancelModalVisible,
        isSubmitting,
        onCancelConfirm: navigateToDetailPage,
        testPrefix: 'EditBaseDetails',
        isDirty,
    };

    const cancelCancellationModal = () => {
        setShowCancelationReason(false);
        setCancelFeeType(null);
    };

    return (
        <>
            <Form
                header={
                    <Header variant="h1">{formValues.activity_name}</Header>
                }
                data-testid="ActivityEditBaseDetailsForm"
                actions={
                    <div className="awsui-util-action-stripe awsui-util-mb-m">
                        <div className="awsui-util-action-stripe-group">
                            <Button
                                variant="link"
                                className="admin-activity-cancel"
                                data-testid="EditBaseDetailsCancel"
                                disabled={isSubmitting}
                                onClick={() => {
                                    setIsCancelModalVisible(true);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="admin-activity-save"
                                data-testid="EditBaseDetailsSave"
                                disabled={isSubmitting}
                                loading={isLoading}
                                onClick={getCancellationModal}
                            >
                                {isSubmitting ? 'Saving' : 'Save'}
                            </Button>
                        </div>
                    </div>
                }
            >
                <EditBaseDetailsFormSection
                    {...editBaseDetailsFormSectionProps}
                />
            </Form>
            <CancelModal {...cancelModalProps} />
            <EditBaseDetailsCancellationModal
                visible={showCancelationReason}
                data-testid="EditBaseDetailsCancellationModal"
                onCancel={cancelCancellationModal}
                onConfirm={handleSubmitEvent}
                setCancellationReason={handleAddCancellationReason}
                cancellationReason={cancellationReason}
                isDataRetained={dontRemoveData}
                isRemoveDataCheckboxVisible={showRemoveDataCheckbox}
                setDontRemoveData={setDontRemoveData}
                cancelFeeType={cancelFeeType}
                setCancelFeeType={setCancelFeeType}
            />
        </>
    );
};

export default EditBaseDetailsForm;
